import { observer } from 'mobx-react'
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import './SignDocumentCore.css'
import { PdfViewer } from '@components/Generic'
import { StoreContext } from '@store/Root.store'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { useSignDocumentPolling } from '@/hooks/useSignDocumentPolling'
import { SignicatBranding } from '@components/SignicatBranding/SignicatBranding'
import { Buttons } from '../SignDocumentCommon/Buttons/Buttons'
import { PageHeader } from '../SignDocumentCommon/PageHeader/PageHeader'
import { SignDocumentBlurOverlay } from '../SignDocumentCommon/SignDocumentBlurOverlay'
import { actionReport } from '@/methods/actionReport'
import { LoadingScreen } from '../Generic/PdfViewer/core/LoadingScreen'
import '../Generic/PdfViewer/core/Core.css'

interface SignDocumentCoreProps {
  groupedDocuments?: boolean // Whether to group documents by groupId and show description in the UI based on the description field from the Document interface
  showBlurOnSuccess?: boolean // Show blur effect after signing
  showButtonHint?: boolean // Show hint for button when disabled
  showRejectButton?: boolean
  showNumberOfDocuments?: boolean // Show number of documents in the proceed button and switch CTA label to `nextDocument` for intermediate documents and `signAllDocuments (N)` for the last one
  showPreviousButton?: boolean // Show previous button to navigate back to already signed documents
  transOverwrite?: {
    header?: string
    subHeader?: string
    checkboxLabel?: string
    checkboxClauseLabel?: string
    proceed?: string
    hint?: string
  }
}

export const SignDocumentCore: FC<SignDocumentCoreProps> = observer(
  ({
    groupedDocuments = false,
    showBlurOnSuccess,
    showButtonHint,
    showRejectButton,
    showNumberOfDocuments,
    showPreviousButton,
    transOverwrite,
  }) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { context } = store.ScenarioState
    const { signDocumentPage, pdfViewer: transViewer } = store.TranslationsState.translations
    const trans = { ...signDocumentPage, ...transOverwrite }

    const checkboxClauseEnabled = !!context?.acceptUnfairClauses
    const showScrollToBottomButton = !!context?.showScrollToBottomButton
    const documentNeedsScrolling = context?.forceScrollingBeforeDocumentAccept

    const [documentError, setDocumentError] = useState<string | null>(null)
    const [allowSigning, setAllowSigning] = useState<boolean>(true)

    const {
      isLoading,
      btnLoading,
      isDocReady,
      pdfFile,
      fileList,
      groups,
      currentDocIndex,
      signSucceeded,
      documentScrolled,
      setDocumentScrolled,
      checkboxKey,
      defaultCheckboxChecked,
      setDefaultCheckboxChecked,
      clauseCheckboxChecked,
      setClauseCheckboxChecked,
      handleSign,
      fetchPreviousDocument,
      isButtonDisabled,
    } = useSignDocumentPolling(
      groupedDocuments,
      checkboxClauseEnabled,
      documentNeedsScrolling,
      showBlurOnSuccess
    )

    const handleCheckboxChange = useCallback(() => {
      if (!pdfFile?.id) return

      setDefaultCheckboxChecked(!defaultCheckboxChecked)

      if (!defaultCheckboxChecked) {
        actionReport({
          type: 'event.onboarding-web.agreement.AGREEMENT_ACKNOWLEDGED',
          payload: {
            name: pdfFile.name,
          },
        })
      } else {
        actionReport({
          type: 'event.onboarding-web.agreement.AGREEMENT_UNACKNOWLEDGED',
          payload: {
            name: pdfFile.name,
          },
        })
      }
    }, [setDefaultCheckboxChecked, defaultCheckboxChecked, pdfFile])

    const handleCheckboxClauseChange = useCallback(() => {
      if (!checkboxClauseEnabled || !pdfFile?.id) return

      setClauseCheckboxChecked(!clauseCheckboxChecked)

      if (!clauseCheckboxChecked) {
        actionReport({
          type: 'event.onboarding-web.agreement.SANTANDER_CLAUSE_CONFIRMED',
          payload: {
            documentId: pdfFile.id,
          },
        })
      }
    }, [checkboxClauseEnabled, setClauseCheckboxChecked, clauseCheckboxChecked, pdfFile])

    useEffect(() => {
      actionReport({
        type: 'event.onboarding-web.eidv.SIGNING_PAGE_OPENED',
        payload: {},
      })
    }, [])

    const proceedButtonText: string = useMemo(() => {
      if (showNumberOfDocuments && fileList.length > 1) {
        if (currentDocIndex === fileList.length - 1) {
          return `${trans.signAllDocuments} (${fileList.length})`
        } else {
          return trans?.nextDocument
        }
      }

      return trans.proceed
    }, [
      showNumberOfDocuments,
      fileList.length,
      currentDocIndex,
      trans.proceed,
      trans.signAllDocuments,
      trans.nextDocument,
    ])

    return (
      <>
        {showBlurOnSuccess && signSucceeded && (
          <SignDocumentBlurOverlay
            fillColor={'var(--icon-custom-color, var(--icon-main-color))'}
            backgroundColor={'var(--icon-background-color, white)'}
          />
        )}

        <div className="sign-doc-core__container sign-document-core">
          <PageHeader
            header={trans.header}
            subHeader={documentNeedsScrolling ? trans.subHeader : ''}
            fileList={fileList}
            currentDocIndex={currentDocIndex}
            groups={groups}
          />

          {isLoading || !isDocReady ? (
            <LoadingScreen>
              <div className="pdf-core__loading-text">{transViewer?.loading}</div>
            </LoadingScreen>
          ) : (
            <PdfViewer
              file={pdfFile?.url || ''}
              fileId={pdfFile?.id}
              fileUrl={pdfFile?.url}
              initialScale={isMobile ? 1.2 : 0.8}
              showScrollToBottomButton={showScrollToBottomButton && !documentScrolled}
              onDocumentLastPage={() => {
                if (!documentScrolled && pdfFile?.id) {
                  setDocumentScrolled(true)

                  actionReport({
                    type: 'event.onboarding-web.agreement.AGREEMENT_SCROLLED_TO_THE_END',
                    payload: {
                      documentId: pdfFile.id,
                      name: pdfFile.name,
                    },
                  })
                }
              }}
              documentError={documentError}
              setDocumentError={(error: string | null) => {
                setDocumentError(error)
                setAllowSigning(false)
              }}
              onDocumentLoadSuccess={() => setAllowSigning(true)}
              setAllowSigning={setAllowSigning}
            />
          )}

          <div className="sign-doc-core__footer">
            <div className="doc-core__checkbox-form">
              <label
                className="label-checkbox"
                onKeyDown={(event) => onKeyDownHandler(event, handleCheckboxChange)}
                tabIndex={0}
              >
                <input
                  key={'checkbox-' + checkboxKey}
                  type="checkbox"
                  name="sign"
                  value={defaultCheckboxChecked.toString()}
                  checked={defaultCheckboxChecked}
                  id="sign"
                  onChange={handleCheckboxChange}
                  tabIndex={-1}
                />
                <span>{trans.checkboxLabel}</span>
              </label>
            </div>
            {checkboxClauseEnabled && (
              <div className="doc-core__checkbox-form">
                <label
                  className="label-checkbox"
                  onKeyDown={(event) => onKeyDownHandler(event, handleCheckboxClauseChange)}
                  tabIndex={0}
                >
                  <input
                    key={'checkbox-clause-' + checkboxKey}
                    type="checkbox"
                    name="signClause"
                    value={clauseCheckboxChecked.toString()}
                    checked={clauseCheckboxChecked}
                    id="signClause"
                    onChange={handleCheckboxClauseChange}
                    tabIndex={-1}
                  />
                  <span>{trans.checkboxClauseLabel}</span>
                </label>
              </div>
            )}

            <div className="sign-doc-core__footer-bottom">
              {!isMobile && (
                <SignicatBranding width="fit-content" margin="0" marginMobile="0" onSignDocument />
              )}

              <Buttons
                documentId={pdfFile?.id ?? ''}
                defaultCheckboxChecked={defaultCheckboxChecked}
                checkboxClauseEnabled={checkboxClauseEnabled}
                clauseCheckboxChecked={clauseCheckboxChecked}
                showButtonHint={showButtonHint}
                showRejectButton={showRejectButton}
                showPreviousButton={currentDocIndex > 0 ? showPreviousButton : false}
                handleSign={handleSign}
                handlePrevious={fetchPreviousDocument}
                setAllowSigning={setAllowSigning}
                isButtonDisabled={isButtonDisabled || !allowSigning}
                isDocReady={isDocReady}
                btnLoading={btnLoading}
                transProceed={proceedButtonText}
                transReject={{
                  rejectButtonText: trans.rejectButtonText,
                  rejectPopupTitle: trans.rejectPopupTitle,
                  backButtonText: trans.backButtonText,
                  confirmButtonText: trans.confirmButtonText,
                }}
                transPrevious={trans.previous}
                transHint={trans.hint}
              />
            </div>
          </div>
        </div>
      </>
    )
  }
)
